import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  getCourseGraph,
  createConceptRelationship,
  updateConceptRelationship,
  deleteConceptRelationship 
} from '../services/dataService';

const GraphView = () => {
  const { courseId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syllabusMissing, setSyllabusMissing] = useState(false); // New state for specific error
  
  // Data state
  const [courseName, setCourseName] = useState('');
  const [concepts, setConcepts] = useState([]);
  const [edges, setEdges] = useState([]);
  const [groupedTopics, setGroupedTopics] = useState([]);

  // UI state
  const [selectedConcept, setSelectedConcept] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Form state for creating a new relationship
  const [newRelationshipTargetId, setNewRelationshipTargetId] = useState('');
  const [newRelationshipType, setNewRelationshipType] = useState('RELATED_TO');
  const [newRelationshipDescription, setNewRelationshipDescription] = useState('');

  // State for the Edit Relationship Modal
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingRelationship, setEditingRelationship] = useState(null);
  const [editedDescription, setEditedDescription] = useState('');

  const relationshipTypes = [
    { value: 'PREREQUISITE_OF', description: 'A → B, (Tiên quyết): Quan hệ có hướng A → B, nghĩa là A là kiến thức tiên quyết của B (cần học A trước B).' },
    { value: 'BROADER_THAN', description: 'A → B, (Narrower): Quan hệ có hướng A → B, nghĩa là A là khái niệm rộng hơn của B (B là phần của A).' },
    { value: 'SAME_AS', description: 'A - B, (Cùng nghĩa): Quan hệ không có hướng, nghĩa là A và B đại diện cho cùng một khái niệm.' },
    { value: 'RELATED_TO', description: 'A - B, (Liên quan): Quan hệ không có hướng, nghĩa là A và B liên quan chủ đề/điều kiện (không có hướng hoặc hai chiều).' }
  ];

  // Fetch graph data from the API
  const fetchGraphData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null); // Reset general error
      setSyllabusMissing(false); // Reset syllabus missing state
      const data = await getCourseGraph(courseId);
      console.log('Fetched graph data:', data);
      setCourseName(data.course.properties.code + '-' + data.course.properties.name || data.course.properties.name_vi || data.course.properties.name_en || 'Unknown Course');
      
      // Separate nodes by label
      const allConcepts = data.nodes.filter(n => n.labels.includes('Concept'));
      const allTopics = data.nodes.filter(n => n.labels.includes('Topic'));

      // Create a map of topics by their ID for easy lookup
      const topicMap = new Map(allTopics.map(t => [t.id, { ...t, concepts: [] }]));

      // Find parent topic for each concept using the edges
      allConcepts.forEach(concept => {
        const edge = data.edges.find(e => e.end_node_id === concept.id && e.type === 'HAS_CONCEPT');
        if (edge) {
          const parentTopic = topicMap.get(edge.start_node_id);
          if (parentTopic) {
            parentTopic.concepts.push(concept);
          }
        }
      });

      // Sort concepts within each topic by short_id
      topicMap.forEach(topic => {
        topic.concepts.sort((a, b) => {
          const aId = a.properties.short_id || '';
          const bId = b.properties.short_id || '';
          return aId.localeCompare(bId, undefined, { numeric: true, sensitivity: 'base' });
        });
      });

      // Convert map to array and sort topics by short_id
      const sortedTopics = Array.from(topicMap.values()).sort((a, b) => {
        const aId = a.properties.short_id || '';
        const bId = b.properties.short_id || '';
        return aId.localeCompare(bId, undefined, { numeric: true, sensitivity: 'base' });
      });

      setGroupedTopics(sortedTopics);
      setConcepts(allConcepts);
      setEdges(data.edges);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch graph data:', err);
      if (err.response?.status === 404 && 
          (err.response?.data?.detail === "Khóa học này chưa có Đề cương giảng dạy" || 
           err.response?.data?.detail === "This course does not have a syllabus yet")) {
        setError("This course does not have a syllabus yet."); // Set a user-friendly message
        setSyllabusMissing(true); // Set specific state
      } else {
        setError(err.response?.data?.detail || err.message || 'Failed to load graph data.');
      }
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    fetchGraphData();
  }, [fetchGraphData]);

  // Filter concepts based on search term
  const filteredConcepts = concepts.filter(concept =>
    (concept.properties.name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Memoize relationship calculation for the selected concept
  const selectedConceptRelationships = React.useMemo(() => {
    if (!selectedConcept) return [];
    return edges.filter(edge => 
      edge.start_node_id === selectedConcept.id || edge.end_node_id === selectedConcept.id
    );
  }, [selectedConcept, edges]);

  // Handler to create a new relationship
  const handleCreateRelationship = async (e) => {
    e.preventDefault();
    if (!selectedConcept || !newRelationshipTargetId) {
      alert('Vui lòng chọn một khái niệm.');
      return;
    }

    try {
      await createConceptRelationship(
        selectedConcept.id,
        parseInt(newRelationshipTargetId),
        newRelationshipType,
        { description: newRelationshipDescription }
      );
      // Reset form and refetch data to show the new relationship
      setNewRelationshipTargetId('');
      setNewRelationshipType('RELATED_TO');
      setNewRelationshipDescription('');
      fetchGraphData(); // Refresh data
    } catch (err) {
      console.error('Failed to create relationship:', err);
      alert(`Error: ${err.response?.data?.detail || err.message}`);
    }
  };

  // Handler to delete a relationship
  const handleDeleteRelationship = async (edgeId) => {
    if (window.confirm('Are you sure you want to delete this relationship?')) {
      try {
        await deleteConceptRelationship(edgeId);
        fetchGraphData(); // Refresh data
      } catch (err) {
        console.error('Failed to delete relationship:', err);
        alert(`Error: ${err.response?.data?.detail || err.message}`);
      }
    }
  };

  // Handlers for the edit modal
  const openEditModal = (relationship) => {
    setEditingRelationship(relationship);
    setEditedDescription(relationship.properties.description || '');
    setIsEditModalOpen(true);
  };

  const handleUpdateRelationship = async () => {
    if (!editingRelationship) return;

    try {
      await updateConceptRelationship(editingRelationship.id, { description: editedDescription });
      setIsEditModalOpen(false);
      setEditingRelationship(null);
      fetchGraphData(); // Refresh data
    } catch (err) {
      console.error('Failed to update relationship:', err);
      alert(`Error: ${err.response?.data?.detail || err.message}`);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-screen"><p className="text-xl text-gray-500">Loading graph data...</p></div>;
  }

  if (syllabusMissing && error) { // Check for the specific syllabus missing error first
    return (
      <div className="h-screen bg-gray-100 font-sans flex flex-col items-center justify-center p-4">
        <div className="p-4 bg-white border-b border-gray-200 fixed top-0 left-0 right-0 z-10">
          <Link 
            to="/"
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
          >
            &larr; Về Trang chủ
          </Link>
        </div>
        <div className="text-center mt-16"> {/* Added margin-top to account for fixed nav bar */}
          <p className="text-xl text-red-500 mb-4">{error}</p>
          <Link 
            to="/"
            className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-lg"
          >
            Về Trang chủ
          </Link>
        </div>
      </div>
    );
  }

  // General error display (if not syllabusMissing)
  if (error) {
    return (
      <div className="h-screen bg-gray-100 font-sans flex flex-col items-center justify-center p-4">
         <div className="p-4 bg-white border-b border-gray-200 fixed top-0 left-0 right-0 z-10">
          <Link 
            to="/"
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
          >
            &larr; Về Trang chủ
          </Link>
        </div>
        <div className="text-center mt-16">
          <p className="text-xl text-red-500">Error: {error}</p>
          <p className="text-md text-gray-600 mt-2">Please try again later or contact support.</p>
        </div>
      </div>
    );
  }

  // Main component render if no errors
  return (
    <div className="h-screen bg-gray-100 font-sans flex flex-col">
      {/* Navigation Bar */}
      <div className="p-4 bg-white border-b border-gray-200">
        <Link 
          to="/"
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
        >
          &larr; Về Trang chủ
        </Link>
      </div>

      {/* Main Content Area */}
      <div className="flex flex-grow overflow-hidden">
        {/* Left Panel: Concept List */}
        <div className="w-1/3 h-full bg-white border-r border-gray-200 flex flex-col">
          <div className="p-4 border-b">
            <h1 className="text-xl font-semibold">Concepts in {courseName}</h1>
            <input 
              type="text"
              placeholder="Tìm kiếm khái niệm..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="overflow-y-auto flex-grow">
            {groupedTopics.map(topic => {
              const filteredConcepts = topic.concepts.filter(concept => 
                (concept.properties.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (concept.properties.short_id || '').toLowerCase().includes(searchTerm.toLowerCase())
              );

              if (filteredConcepts.length === 0 && searchTerm) {
                return null; // Don't render topic if no concepts match search
              }

              return (
                <div key={topic.id} className="p-2">
                  <h2 className="font-bold text-gray-700 px-2 py-1">{topic.properties.short_id} - {topic.properties.title}</h2>
                  <ul>
                    {filteredConcepts.map(concept => (
                      <li 
                        key={concept.id}
                        onClick={() => setSelectedConcept(concept)}
                        className={`p-2 my-1 rounded-md cursor-pointer transition-colors ${
                          selectedConcept?.id === concept.id 
                            ? 'bg-blue-500 text-white' 
                            : 'hover:bg-gray-200'
                        }`}>
                        {concept.properties.short_id} - {concept.properties.title}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right Panel: Relationship Editor */}
        <div className="w-2/3 h-full p-6 overflow-y-auto">
          {selectedConcept ? (
            <div>
              <h2 className="text-2xl font-bold mb-4">
                Chỉnh sửa mối quan hệ của <span className="text-blue-600">{ selectedConcept.properties.short_id + ' - ' + selectedConcept.properties.title}</span>
              </h2>
              
              {/* Create Relationship Form */}
              <div className="bg-white p-4 rounded-lg shadow-md mb-6">
                <h3 className="text-lg font-semibold mb-3">Tạo mối quan hệ mới</h3>
                <form onSubmit={handleCreateRelationship} className="space-y-4">                  
                  <div>
                    <label htmlFor="relationship-type" className="block text-sm font-medium text-gray-700">Loại mối quan hệ</label>
                    <select
                      id="relationship-type"
                      value={newRelationshipType}
                      onChange={(e) => setNewRelationshipType(e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    >
                      {relationshipTypes.map(rt => (
                        <option key={rt.value} value={rt.value}>{rt.value}</option>
                      ))}
                    </select>
                    {newRelationshipType && (
                      <p className="mt-1 text-xs text-gray-600">
                        {relationshipTypes.find(rt => rt.value === newRelationshipType)?.description}
                      </p>
                    )}
                  </div>
                  <div>
                    <label htmlFor="target-concept" className="block text-sm font-medium text-gray-700">Khái niệm mục tiêu</label>
                    <select
                      id="target-concept"
                      value={newRelationshipTargetId}
                      onChange={(e) => setNewRelationshipTargetId(e.target.value)}
                      className={`mt-1 block w-full px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${newRelationshipTargetId === '' ? 'text-gray-500' : ''}`}
                    >
                      <option value="" disabled className="text-gray-500">Chọn một khái niệm...</option>
                      {groupedTopics.map(topic => (
                        <optgroup key={topic.id} label={`${topic.properties.short_id} - ${topic.properties.title}`}>
                          {topic.concepts
                            .filter(c => c.id !== selectedConcept?.id) // Exclude self-relationship
                            .map(concept => (
                              <option key={concept.id} value={concept.id} className="text-black">
                                {concept.properties.short_id} - {concept.properties.title}
                              </option>
                            ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                  {/* <div>
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700">Mô tả (Tùy chọn)</label>
                    <input
                      type="text"
                      id="description"
                      value={newRelationshipDescription}
                      onChange={(e) => setNewRelationshipDescription(e.target.value)}
                      placeholder="ví dụ: 'Builds upon the principles of...'"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div> */}
                  <div className="flex justify-end">
                    <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                      Tạo mối quan hệ
                    </button>
                  </div>
                </form>
              </div>

              {/* Existing Relationships Table */}
              <div className="bg-white p-4 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-3">Mối quan hệ hiện có</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Điểm đến</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Khái niệm liên quan</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Loại mối quan hệ</th>
                        {/* <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mô tả</th> */}
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hành động</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {selectedConceptRelationships.filter(edge => edge.start_node_id === selectedConcept.id).length > 0 ? (
                        selectedConceptRelationships
                          .filter(edge => edge.start_node_id === selectedConcept.id)
                          .map(edge => {
                            const relatedConceptId = edge.end_node_id;
                            const relatedConcept = concepts.find(c => c.id === relatedConceptId);

                            return (
                              <tr key={edge.id}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">Outgoing</span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{relatedConcept?.properties.short_id + ' - ' + relatedConcept?.properties.title || 'Unknown Concept'}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{edge.type}</td>
                                {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 truncate max-w-xs">{edge.properties.description}</td> */}
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                                  {/* <button onClick={() => openEditModal(edge)} className="text-indigo-600 hover:text-indigo-900">Edit</button> */}
                                  <button onClick={() => handleDeleteRelationship(edge.id)} className="text-red-600 hover:text-red-900">Delete</button>
                                </td>
                              </tr>
                            );
                          })
                      ) : (
                        <tr>
                          <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500"><strong>Không có mối quan hệ nào.</strong></td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex justify-center items-center h-full">
              <p className="text-xl text-gray-500">Chọn một khái niệm từ bên trái để bắt đầu chỉnh sửa mối quan hệ.</p>
            </div>
          )}
        </div>

        {/* Edit Relationship Modal */}
        {isEditModalOpen && editingRelationship && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
            <div className="relative mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
              <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">Edit Relationship Description</h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="edit-description" className="block text-sm font-medium text-gray-700">Mô tả</label>
                  <input
                    type="text"
                    id="edit-description"
                    value={editedDescription}
                    onChange={(e) => setEditedDescription(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <button 
                    onClick={() => setIsEditModalOpen(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                  >
                    Hủy
                  </button>
                  <button 
                    onClick={handleUpdateRelationship}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Lưu thay đổi
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GraphView;
